import isNil from 'lodash/isNil';
import isPlainObject from 'lodash/isPlainObject';
import md5 from 'md5';
import stableStringify from 'json-stable-stringify';

/**
 * Recursively removes all properties with null or undefined values from an object and its nested objects.
 *
 * @template T - The type of the input object, extending Obj
 * @param obj - The object from which to deeply omit nil (null or undefined) values
 * @returns A new object with the same type as the input, but with all null and undefined properties removed at all levels
 *
 * @example
 * ```typescript
 * const input = { a: 1, b: null, c: { d: 'hello', e: undefined, f: { g: null, h: 'world' } } };
 * const result = deepOmitNilValues(input);
 * // result: { a: 1, c: { d: 'hello', f: { h: 'world' } } }
 * ```
 */
export function deepOmitNilValues<T extends Obj>(obj: T | null | undefined): Partial<T> {
  if (!obj) {
    return {};
  }

  const result: any = {};

  for (const [key, value] of Object.entries(obj)) {
    if (!isNil(value)) {
      if (isPlainObject(value)) {
        const nestedResult = deepOmitNilValues(value);
        if (Object.keys(nestedResult).length > 0) {
          result[key] = nestedResult;
        }
      } else {
        result[key] = value;
      }
    }
  }

  return result;
}

/**
 * Generates a consistent hash string for a given object.
 * @param obj The object to hash.
 * @returns An MD5 hash string representing the object.
 */
export function getObjectMd5Hash(obj: Obj): string {
  return md5(stableStringify(obj));
}
